<template>
  <div class="flex h-full w-full items-center justify-between gap-8 bg-gray-900 px-4 text-white">
    <!-- Dashboard Item -->
    <div
      class="flex flex-1 cursor-pointer items-center justify-center rounded-md py-1"
      @click="onItemClick({ id: DASHBOARD_ID, label: 'Dashboard' })"
    >
      <a
        href="/home2"
        :class="[
          'flex w-full flex-col items-center justify-center',
          `${selectedItem === DASHBOARD_ID ? 'bg-blue-500' : 'hover:bg-gray-700'} rounded-md p-1`
        ]"
      >
        <DashboardIcon
          :fillColor="selectedItem === DASHBOARD_ID ? '#fff' : undefined"
          width="16"
          height="16"
        />
        <span class="text-xs">Dashboard</span>
      </a>
    </div>

    <!-- Coach AI Item -->
    <Button variant="coach" size="icon" class="flex-shrink-0" @click="onCoachAIClick">
      <CoachAIIcon height="40" width="40" />
    </Button>

    <!-- Menu Button -->
    <div class="flex flex-1 justify-center">
      <div
        class="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg py-2 text-white"
        @click="toggleDrawer"
      >
        <MenuIcon width="16" height="16" />
        <span class="text-xs text-white">Menu</span>
      </div>
    </div>

    <!-- Mobile Drawer -->
    <Drawer :open="isDrawerOpen" @update:open="isDrawerOpen = $event">
      <DrawerContent class="!h-full !max-h-screen !rounded-none !bg-gray-900">
        <div class="relative flex h-full w-full flex-col p-4 text-white">
          <DrawerTitle class="mb-6 flex items-center justify-between text-white">
            <NavigationPaneBrand :expanded="true" />
            <button class="rounded-full p-2 hover:bg-gray-700" @click="toggleDrawer">
              <CloseIcon class="h-6 w-6" />
            </button>
          </DrawerTitle>

          <div class="flex flex-1 flex-col justify-between overflow-y-auto">
            <NavigationWrapper
              :items="items"
              position="left"
              :expanded="true"
              :selectedItem="selectedItem"
              :showDashboard="false"
              :showCoachAI="showCoachAI"
              :isMobileNav="true"
              @selectItem="onSelectItemFromDrawer"
            />

            <NavigationPaneUtils
              :showSettings="showSettings"
              :showAuth="showAuth"
              :isLoggedIn="isLoggedIn"
              :canExpand="false"
              position="left"
              :expanded="true"
            />
            <div class="absolute bottom-32 left-1/2 -translate-x-1/2 opacity-5">
              <AceLogoIcon :width="144" :height="134" />
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { NavItem } from '../types/NavigationPane'
import { DASHBOARD_ID } from '../constants/navigationPaneDefaults'
import NavigationPaneBrand from './NavigationPaneBrand.vue'
import NavigationWrapper from './NavigationWrapper.vue'
import NavigationPaneUtils from './NavigationPaneUtils.vue'
import { DashboardIcon, CoachAIIcon, MenuIcon, CloseIcon, AceLogoIcon } from '../../icons'
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer'
import type { Profile } from '../NavigationPaneComponent.vue'
import { mountChatSidebarWebComponent } from '@/helpers/integrations/chatSidebar'
import Button from '@/components/ui/button/Button.vue'

interface Props {
  items: NavItem[]
  selectedItem?: string
  showSettings?: boolean
  showAuth?: boolean
  isLoggedIn?: boolean
  showCoachAI?: boolean
  profile: Profile
}

withDefaults(defineProps<Props>(), {
  selectedItem: undefined,
  showSettings: false,
  showAuth: true,
  isLoggedIn: false,
  showCoachAI: true
})

const emit = defineEmits<{
  setSelectedItem: [id: string]
}>()

const isDrawerOpen = ref(false)

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value
}

const onItemClick = (item: NavItem) => {
  emit('setSelectedItem', item.id)
}

const onCoachAIClick = () => {
  mountChatSidebarWebComponent({
    mode: 'sidebar-mode'
  })
}

const onSelectItemFromDrawer = (id: string) => {
  emit('setSelectedItem', id)
  isDrawerOpen.value = false
}
</script>

<style scoped>
/* Force drawer to take full height and proper styling */
:deep(.vaul-drawer-content) {
  max-height: calc(100vh - 80px) !important;
  height: 100vh !important;
}
</style>
